/**
 * Baby Context
 * Manages baby profiles and active baby selection
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getUserBabies,
  createBaby as createBabyService,
  updateBaby as updateBabyService,
  deleteBaby as deleteBabyService,
  getActiveBabyId,
  setActiveBabyId as setActiveBabyIdService,
} from '@/services/baby.service';
import type { Baby, BabyInput } from '@/types/baby';

interface BabyContextType {
  babies: Baby[];
  activeBaby: Baby | null;
  loading: boolean;
  setActiveBaby: (babyId: string) => Promise<void>;
  addBaby: (babyData: BabyInput) => Promise<Baby>;
  updateBaby: (babyId: string, updates: Partial<BabyInput>) => Promise<void>;
  deleteBaby: (babyId: string) => Promise<void>;
  refreshBabies: () => Promise<void>;
}

const BabyContext = createContext<BabyContextType | undefined>(undefined);

export function BabyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [babies, setBabies] = useState<Baby[]>([]);
  const [activeBaby, setActiveBabyState] = useState<Baby | null>(null);
  const [loading, setLoading] = useState(true);

  // Load babies when user logs in
  useEffect(() => {
    if (user) {
      loadBabies();
    } else {
      setBabies([]);
      setActiveBabyState(null);
      setLoading(false);
    }
  }, [user]);

  // Load babies from Firestore
  const loadBabies = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userBabies = await getUserBabies(user.uid);
      setBabies(userBabies);

      const activeBabyId = await getActiveBabyId(user.uid);

      if (activeBabyId) {
        const active = userBabies.find((b) => b.id === activeBabyId);
        setActiveBabyState(active || null);
      } else if (userBabies.length > 0) {
        setActiveBabyState(userBabies[0]);
        await setActiveBabyIdService(user.uid, userBabies[0].id);
      } else {
        setActiveBabyState(null);
      }
    } catch (error) {
      console.error('[BabyContext] Error loading babies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set active baby
  const setActiveBaby = async (babyId: string) => {
    if (!user) return;

    const baby = babies.find((b) => b.id === babyId);
    if (!baby) {
      console.error('Baby not found:', babyId);
      return;
    }

    try {
      setActiveBabyState(baby);
      await setActiveBabyIdService(user.uid, babyId);
    } catch (error) {
      console.error('Error setting active baby:', error);
      throw error;
    }
  };

  // Add new baby
  const addBaby = async (babyData: BabyInput): Promise<Baby> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const newBaby = await createBabyService(user.uid, babyData);
      setBabies((prev) => [newBaby, ...prev]);

      // Always set the newly created baby as active
      setActiveBabyState(newBaby);
      await setActiveBabyIdService(user.uid, newBaby.id);

      return newBaby;
    } catch (error) {
      console.error('Error adding baby:', error);
      throw error;
    }
  };

  // Update baby
  const updateBaby = async (
    babyId: string,
    updates: Partial<BabyInput>
  ): Promise<void> => {
    try {
      await updateBabyService(babyId, updates);

      // Refresh babies list
      await loadBabies();
    } catch (error) {
      console.error('Error updating baby:', error);
      throw error;
    }
  };

  // Delete baby (soft delete)
  const deleteBaby = async (babyId: string): Promise<void> => {
    try {
      await deleteBabyService(babyId);

      // Remove from local state
      setBabies((prev) => prev.filter((b) => b.id !== babyId));

      // If deleted baby was active, switch to another
      if (activeBaby?.id === babyId) {
        const remaining = babies.filter((b) => b.id !== babyId);
        if (remaining.length > 0 && user) {
          setActiveBabyState(remaining[0]);
          await setActiveBabyIdService(user.uid, remaining[0].id);
        } else {
          setActiveBabyState(null);
        }
      }
    } catch (error) {
      console.error('Error deleting baby:', error);
      throw error;
    }
  };

  // Refresh babies list
  const refreshBabies = async () => {
    await loadBabies();
  };

  const value: BabyContextType = {
    babies,
    activeBaby,
    loading,
    setActiveBaby,
    addBaby,
    updateBaby,
    deleteBaby,
    refreshBabies,
  };

  return <BabyContext.Provider value={value}>{children}</BabyContext.Provider>;
}

export function useBaby() {
  const context = useContext(BabyContext);
  if (context === undefined) {
    throw new Error('useBaby must be used within a BabyProvider');
  }
  return context;
}
